/**
 * @Author: yabingzyb.zhang
 * @Date: 2019-06-26 12:01:05
 * @Last Modified by: yabingzyb.zhang
 * @description 曝光组件（配置DeviceEventEmitter使用）
 * @description 可是在listView或者scrollView的onScroll中派发事件来检查元素位置
 * @description 可以通过设置autoExposure为true进行自动曝光埋点
 * @providesModule QExposureContainerX
 */
import React, { PureComponent } from 'react';
import { View, Dimensions, DeviceEventEmitter } from 'react-native';
import PropTypes from 'prop-types';
export const Events = {
    RequireUpdate: '__exposure_events_requireUpdate__'
};

export const RaiseFrom = {
    InitialState: 'InitialState',
    RelayoutState: 'RelayoutState',
    RealTimeState: 'RealTimeState',
    FinalState: 'FinalState'
};

// 滑动方向
const DIRECTS = {
    UP: 'UP',
    DOWN: 'DOWN'
};
const window = Dimensions.get('window');

export default class ExposureContainer extends PureComponent {
    static propTypes = {
        // 检查元素出现的方向：0: 所有方向，1：从下向上，2：从上向下，3：垂直方向
        type: PropTypes.number,
        // 元素曝光时的回调
        onExposure: PropTypes.func,
        // 是否是首次曝光模式
        firstExposure: PropTypes.bool,
        // 点击时的曝光埋点数据
        clickLog: PropTypes.shape({
            // 埋点的页面
            page: PropTypes.string,
            // 埋点的行为
            action: PropTypes.string,
            // 埋点的扩展信息
            data: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.object
            ])
        }),
        // 组件展示本身高度的多少认为已曝光
        percent: PropTypes.number,
        // 额外参数，在曝光时会传入回调函数
        extraData: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.object
        ])
    };

    static defaultProps = {
        type: 0,
        firstExposure: true,
        clickLog: {
            page: '__exposure__component__click__',
            action: '__exposure_action__click__',
            data: {}
        },
        onExposure: () => {
        },
        percent: 1,
        extraData: null
    };

    // 事件监听句柄
    subscription = null;
    // 是否已经曝光过
    hasStatsCollected = false;
    // 上次曝光事件
    lastRTUpdateTime = 0;
    // 上次的状态
    preStatus = null;
    // 元素状态
    eleInfo = {};

    constructor(props) {
        super(props);

        this.state = {
            rectTop: 0,
            rectBottom: 0,
            rectWidth: 0,
            verticalDirect: ''
        };

        this.viewPort = null;

        this.onUpdateRequired = this.onUpdateRequired.bind(this);
        this.onLayout = this.onLayout.bind(this);
    }

    componentDidMount() {
        this.subscription = DeviceEventEmitter.addListener(Events.RequireUpdate, this.onUpdateRequired);
    }

    componentWillUnmount() {
        this.subscription && this.subscription.remove();
        this.hasStatsCollected = false;
        this.lastRTUpdateTime = 0;
    }

    render() {
        const { children, ...props } = this.props;

        return (
            <View
                collapsable={false}
                ref={component => {
                    this.viewPort = component;
                }}
                onLayout={this.onLayout}
                {...props}>
                {children}
            </View>
        );
    }

    onUpdateRequired = ({ raiseFrom }) => {
        // 滚动时如果事件间隔小于300毫秒，则不进行检查
        const isTooFast = raiseFrom === RaiseFrom.RealTimeState && Date.now() - this.lastRTUpdateTime < 300;

        if (!this.viewPort || isTooFast || this.hasStatsCollected) {
            return;
        }

        // 保存上次的更新时间
        this.lastRTUpdateTime = Date.now();

        const { percent } = this.props;
        this.viewPort.measure((x = 0, y = 0, width = 0, height = 0, pageX = 0, pageY = 0) => {
            try {
                // 按照传进来的百分比计算曝光
                let computeHeight = height * percent;
                let computeWidth = width * percent;

                const { rectBottom, verticalDirect: prevVerticalDirect } = this.eleInfo,
                    offsetY = rectBottom ? rectBottom - (pageY + computeHeight) : 0,
                    verticalDirect = offsetY === 0 ? prevVerticalDirect : (offsetY > 0 ? DIRECTS.DOWN : DIRECTS.UP);

                this.eleInfo = {
                    rectTop: pageY,
                    rectBottom: pageY + computeHeight,
                    rectWidth: pageX + computeWidth,
                    verticalDirect,
                    selfHeight: height,
                    selfWidth: width,
                    raiseFrom
                };

                this.isInViewPort();
            } catch (e) {
                console.warn('[Exposure]: get measure failed', e);
            }
        });
    };

    /**
     * 检查元素位置
     */
    isInViewPort = () => {
        const info = this.eleInfo,
            { type = 0, onExposure, extraData, firstExposure/* , autoExposure, exposureLog */ } = this.props,
            { preStatus } = this;

        let isVisible;

        switch (type) {
            case 0:
                isVisible = this.checkAll(info);
                break;
            case 1:
                isVisible = this.checkVerticalUp(info);
                break;
            case 2:
                isVisible = this.checkVerticalDown(info);
                break;
            case 3:
                isVisible = this.checkVertical(info);
                break;
            default:
                isVisible = false;
        }

        // 将isVisible转换为Boolean类型
        isVisible = !!isVisible;

        // 埋点回调的数据格式
        const data = {
            data: extraData,
            status: isVisible
        };

        // 是否是首次曝光
        if (firstExposure) {
            // 需要首次曝光时，首次展示时进行回调通知
            if (isVisible) {
                _isFunction(onExposure) && onExposure(data, info);
                this.hasStatsCollected = true;
                // autoExposure && this._printLog(exposureLog);
            }
        } else {
            // 非首次埋点的话，只要状态变化就进行通知
            if (preStatus !== isVisible) {
                _isFunction(onExposure) && onExposure(data, info);
                this.preStatus = isVisible;
                // autoExposure && isVisible && this._printLog(exposureLog);
            }
        }
    };

    /**
     * 检查元素在整个页面中是否出现
     * @param {object} state
     */
    checkAll(state) {
        const { rectBottom = 0, rectTop = 0, rectWidth = 0 } = state,
            { height, width } = window;

        return rectBottom &&
            rectTop >= 0 &&
            rectBottom <= height &&
            rectWidth > 0 &&
            rectWidth <= width;
    }

    /**
     * 检查元素在垂直方向是否出现在视口中，从下向上滑动
     * @param {object} state
     */
    checkVerticalUp(state) {
        const { rectBottom = 0, rectTop = 0, verticalDirect } = state,
            { height } = window,
            direction = !verticalDirect || verticalDirect === DIRECTS.DOWN;

        return rectBottom &&
            rectTop >= 0 &&
            direction &&
            rectBottom <= height;
    }


    /**
     * 检查元素在垂直方向是否出现在视口中，从上向下滑动
     * @param {object} state
     */
    checkVerticalDown(state) {
        const { rectBottom = 0, rectTop = 0, verticalDirect } = state,
            { height } = window,
            direction = !verticalDirect || verticalDirect === DIRECTS.UP;

        return rectBottom &&
            rectTop >= 0 &&
            direction &&
            rectBottom <= height;
    }

    /**
     * 检查元素在垂直方向是否出现在视口中
     * @param {object} state
     */
    checkVertical(state) {
        const { rectBottom = 0, rectTop = 0 } = state,
            { height } = window;

        return rectBottom &&
            rectTop >= 0 &&
            rectBottom <= height;
    }

    onLayout = () => {
        this.onUpdateRequired({ raiseFrom: RaiseFrom.RelayoutState, extra: '__exposure_layout' });
    };
}

/**
 * 是否是函数
 * @param func
 * @return {boolean}
 * @private
 */
function _isFunction(func) {
    return typeof func == 'function' || false;
}
